import { useState } from 'react'
import {
  Box, Button, CloseButton, Dialog, Field, HStack, IconButton, Menu, Portal, Text, Textarea, VStack,
} from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { FiChevronDown, FiChevronRight, FiMoreVertical, FiSlash, FiTrash2, FiUnlock } from 'react-icons/fi'
import { useBlockContact, useUnblockContact, useAdminRevokeGrant } from '../../../hooks/useAdminGrants'
import { extractApiMessage } from '../../../lib/apiError'
import { ResponsiveTable, ResponsiveModal, StatusChip, PersonAvatar, type ResponsiveColumn, type StatusVariant } from '../../shared'
import { AdminGrantSessionRows } from './AdminGrantSessionRows'
import type { AdminGrantDisplayRow, AdminGrantKind } from '../../../types/adminGrant'
import type { CanvasserGrantStatus } from '../../../types/canvasserGrant'

// L11 slice 3b, kind-parameterized in LW-24 Slice 3 Task 3: the committee-wide Admin grant table,
// shared by the canvasser grid and the attendee grid via the `kind` prop. Sibling of 1e's
// components/campaign/canvassers/CanvasserRosterTable.tsx -- same ResponsiveTable + single
// per-row overflow Menu + expand-row device panel shape -- but committee-wide (the group --
// campaign or event -- is a COLUMN here, not the route) and with the person-level block/unblock
// actions that only Admin has. Block targets contactId, NOT grantId: the same person can hold
// grants on several campaigns/events and a block bars all of them at once.

interface AdminGrantTableProps {
  kind: AdminGrantKind
  rows: AdminGrantDisplayRow[]
}

// Status idiom, identical to 1e's roster (spec §4F): info for Active, warning for Expired,
// danger for Revoked/Blocked. StatusChip owns the bg/fg token pairs.
const STATUS_VARIANT: Record<CanvasserGrantStatus, StatusVariant> = {
  Active: 'info',
  Expired: 'warning',
  Revoked: 'danger',
  Blocked: 'danger',
}

export function AdminGrantTable({ kind, rows }: AdminGrantTableProps) {
  const noun = kind === 'canvasser' ? 'canvasser' : 'attendee'
  const blockContact = useBlockContact()
  const unblockContact = useUnblockContact()
  const revokeGrant = useAdminRevokeGrant(kind)

  const [expandedGrantId, setExpandedGrantId] = useState<string | null>(null)
  const [blockTarget, setBlockTarget] = useState<AdminGrantDisplayRow | null>(null)
  const [blockReason, setBlockReason] = useState('')
  const [blockError, setBlockError] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<AdminGrantDisplayRow | null>(null)
  const [removeReason, setRemoveReason] = useState('')
  const [removeError, setRemoveError] = useState<string | null>(null)

  const toggleExpanded = (row: AdminGrantDisplayRow) =>
    setExpandedGrantId((cur) => (cur === row.grantId ? null : row.grantId))

  const openBlock = (row: AdminGrantDisplayRow) => {
    setBlockTarget(row)
    setBlockReason('')
    setBlockError(null)
  }

  const closeBlock = () => {
    setBlockTarget(null)
    setBlockReason('')
    setBlockError(null)
  }

  const confirmBlock = async () => {
    if (!blockTarget) return
    setBlockError(null)
    try {
      await blockContact.mutateAsync({
        contactId: blockTarget.contactId,
        reasonNote: blockReason.trim() || undefined,
      })
      toaster.create({ type: 'success', title: `${blockTarget.fullName} blocked` })
      closeBlock()
    } catch (err) {
      setBlockError(extractApiMessage(err, 'Failed to block this person.'))
    }
  }

  const handleUnblock = async (row: AdminGrantDisplayRow) => {
    try {
      await unblockContact.mutateAsync(row.contactId)
      toaster.create({ type: 'success', title: `${row.fullName} unblocked` })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to unblock this person.') })
    }
  }

  const openRemove = (row: AdminGrantDisplayRow) => {
    setRemoveTarget(row)
    setRemoveReason('')
    setRemoveError(null)
  }

  const closeRemove = () => {
    setRemoveTarget(null)
    setRemoveReason('')
    setRemoveError(null)
  }

  const confirmRemove = async () => {
    if (!removeTarget) return
    setRemoveError(null)
    try {
      await revokeGrant.mutateAsync({
        grantId: removeTarget.grantId,
        // The server rejects a None/undefined reason code with a 400, so always send one.
        body: { reasonCode: 'AdminBlocked', reasonNote: removeReason.trim() || undefined },
      })
      toaster.create({ type: 'success', title: `${removeTarget.fullName} removed` })
      closeRemove()
    } catch (err) {
      setRemoveError(extractApiMessage(err, `Failed to remove this ${noun}.`))
    }
  }

  const columns: ResponsiveColumn<AdminGrantDisplayRow>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (r) => (
        <HStack gap={1}>
          <IconButton
            aria-label={expandedGrantId === r.grantId ? `Collapse devices for ${r.fullName}` : `Show devices for ${r.fullName}`}
            variant="ghost"
            size="sm"
            minW="44px"
            minH="44px"
            _touch={{ minW: '44px', minH: '44px' }}
            onClick={(e) => { e.stopPropagation(); toggleExpanded(r) }}
          >
            {expandedGrantId === r.grantId ? <FiChevronDown /> : <FiChevronRight />}
          </IconButton>
          {kind === 'canvasser' && <PersonAvatar name={r.fullName} src={r.portraitUrl} size="sm" />}
          <Text fontWeight="medium">{r.fullName}</Text>
        </HStack>
      ),
    },
    {
      header: kind === 'canvasser' ? 'Campaign' : 'Event',
      mobileRole: 'meta',
      cell: (r) => <Text fontSize="sm">{r.groupName || '—'}</Text>,
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (r) => <StatusChip variant={STATUS_VARIANT[r.status]}>{r.status}</StatusChip>,
    },
    {
      header: 'Devices',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (r) => r.activeSessionCount,
    },
    {
      header: 'Last seen',
      mobileRole: 'meta',
      cell: (r) => (
        <Text fontSize="sm" color="muted">
          {r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleDateString() : 'Never'}
        </Text>
      ),
    },
    {
      header: 'Actions',
      mobileRole: 'meta',
      cell: (r) => (
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton
              aria-label="Grant actions"
              variant="ghost"
              size="sm"
              minW="44px"
              minH="44px"
              _touch={{ minW: '44px', minH: '44px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content onClick={(e) => e.stopPropagation()}>
                {r.status === 'Blocked' ? (
                  <Menu.Item value="unblock" onSelect={() => handleUnblock(r)}>
                    <HStack gap={2}><FiUnlock /><Text>Unblock person</Text></HStack>
                  </Menu.Item>
                ) : (
                  <Menu.Item value="block" color="danger.fg" onSelect={() => openBlock(r)}>
                    <HStack gap={2}><FiSlash /><Text>Block person</Text></HStack>
                  </Menu.Item>
                )}
                <Menu.Item value="remove" color="danger.fg" onSelect={() => openRemove(r)}>
                  <HStack gap={2}><FiTrash2 /><Text>Remove {noun}</Text></HStack>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      ),
    },
  ]

  return (
    <Box>
      <ResponsiveTable
        columns={columns}
        rows={rows}
        keyFn={(r) => r.grantId}
        emptyMessage={`No ${noun} grants match these filters.`}
        bare
        expandedRowKey={expandedGrantId}
        renderExpanded={(r) => <AdminGrantSessionRows kind={kind} grantId={r.grantId} />}
      />

      <ResponsiveModal isOpen={!!blockTarget} onClose={closeBlock} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Block {blockTarget?.fullName}?</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                {!!blockError && <Text color="danger.fg" fontSize="sm">{blockError}</Text>}
                <Text fontSize="sm" color="muted">
                  This bars this person from every campaign and signs their devices out now. It is
                  reversible — unblocking lets them sign in again with the same link or code.
                </Text>
                <Field.Root>
                  <Field.Label>Reason (optional)</Field.Label>
                  <Textarea size="sm" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" minH="44px" onClick={closeBlock}>Cancel</Button>
                <Button
                  variant="outline"
                  borderColor="danger.fg"
                  color="danger.fg"
                  size="sm"
                  minH="44px"
                  onClick={confirmBlock}
                  loading={blockContact.isPending}
                >
                  Block person
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      <ResponsiveModal isOpen={!!removeTarget} onClose={closeRemove} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Remove {removeTarget?.fullName}?</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                {!!removeError && <Text color="danger.fg" fontSize="sm">{removeError}</Text>}
                <Text fontSize="sm" color="muted">
                  This revokes this grant permanently and signs out every device on it. Unlike a
                  block it cannot be undone — the person needs a fresh grant to come back.
                </Text>
                <Field.Root>
                  <Field.Label>Reason (optional)</Field.Label>
                  <Textarea size="sm" value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} />
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" minH="44px" onClick={closeRemove}>Cancel</Button>
                <Button
                  variant="outline"
                  borderColor="danger.fg"
                  color="danger.fg"
                  size="sm"
                  minH="44px"
                  onClick={confirmRemove}
                  loading={revokeGrant.isPending}
                >
                  Remove {noun}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Box>
  )
}
